import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from "@mui/material";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { faGithub } from "@fortawesome/free-brands-svg-icons";
import projectsData from "../../data/projects";

const Projects = ({
  limit = null,
  featuredOnly = false,
  showHeader = true,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("All");

  const availableProjects = featuredOnly
    ? projectsData.filter((project) => project.featured)
    : projectsData;

  const categories = [
    "All",
    ...new Set(availableProjects.map((item) => item.category)),
  ];

  const filteredProjects = useMemo(() => {
    let result = [...availableProjects];

    if (filter !== "All") {
      result = result.filter((item) => item.category === filter);
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          item.description.toLowerCase().includes(q) ||
          item.category.toLowerCase().includes(q) ||
          item.tags.some((tag) => tag.toLowerCase().includes(q))
      );
    }

    if (limit) {
      result = result.slice(0, limit);
    }

    return result;
  }, [filter, searchTerm, limit, availableProjects]);

  return (
    <Box id="projects" sx={{ py: 8 }}>
      {showHeader && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h2"
            sx={{
              mb: 1,
              background: "linear-gradient(135deg, #2563eb, #7c3aed)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Projects I’ve Worked On
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
            A curated list of product, academic, portfolio, and business website
            projects, with TapDosh positioned as the standout flagship build.
          </Typography>
        </Box>
      )}

      <Stack spacing={2} sx={{ mb: 4 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, value) => value && setFilter(value)}
          sx={{ flexWrap: "wrap", gap: 1 }}
        >
          {categories.map((item) => (
            <ToggleButton
              key={item}
              value={item}
              sx={{
                borderRadius: "999px !important",
                px: 2,
                textTransform: "none",
              }}
            >
              {item}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <TextField
          fullWidth
          placeholder="Search by title, tag, or category"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FontAwesomeIcon icon={faSearch} />
              </InputAdornment>
            ),
            sx: { borderRadius: 3 },
          }}
        />
      </Stack>

      <Grid container spacing={3}>
        {filteredProjects.map((project, index) => (
          <Grid item xs={12} md={6} lg={4} key={project.id}>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.04 }}
            >
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 4,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: project.featured ? "primary.main" : "divider",
                  boxShadow: project.featured
                    ? "0 18px 45px rgba(37,99,235,0.15)"
                    : "0 10px 28px rgba(0,0,0,0.06)",
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 22px 50px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    color: "white",
                    minHeight: 120,
                    background: `linear-gradient(135deg, ${project.accentFrom}, ${project.accentTo})`,
                  }}
                >
                  <Chip
                    label={project.shortLabel}
                    size="small"
                    sx={{
                      mb: 1.5,
                      backgroundColor: "rgba(255,255,255,0.15)",
                      color: "white",
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, lineHeight: 1.2 }}
                  >
                    {project.title}
                  </Typography>
                  <Typography sx={{ opacity: 0.9, mt: 0.5 }}>
                    {project.category}
                  </Typography>
                </Box>

                <CardContent sx={{ p: 3 }}>
                  <Typography
                    color="text.secondary"
                    sx={{ lineHeight: 1.75, mb: 2 }}
                  >
                    {project.description}
                  </Typography>

                  <Box
                    component="ul"
                    sx={{ pl: 2.3, mb: 2, color: "text.secondary" }}
                  >
                    {project.highlights.map((item) => (
                      <Typography
                        key={item}
                        component="li"
                        variant="body2"
                        sx={{ mb: 0.6, lineHeight: 1.7 }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mb: 3 }}
                  >
                    {project.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1.2}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {project.live && (
                      <Button
                        variant="contained"
                        component="a"
                        href={project.live}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<FontAwesomeIcon icon={faExternalLinkAlt} />}
                      >
                        Live Site
                      </Button>
                    )}

                    {project.github && (
                      <Button
                        variant="outlined"
                        component="a"
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<FontAwesomeIcon icon={faGithub} />}
                      >
                        View Code
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {filteredProjects.length === 0 && (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="h6">No projects matched your search.</Typography>
          <Typography color="text.secondary">
            Try another keyword or switch category.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Projects;
